import * as React from 'react';

import { enumSidecarOperatingSystem } from '@rematter/frontend-lib-api-client';
import {
  ContentSection,
  LocalizedDate,
  Select,
  Switch,
  TextField,
} from '@rematter/ui-web';
import { cn } from '@rematter/ui-web/lib';
import { useAtomValue, useSetAtom } from 'jotai';
import { selectAtom } from 'jotai/utils';
import isEqual from 'lodash/isEqual';

import {
  $sidecarForm,
  SidecarFormState,
} from '../../../_state/sidecarForm.state';
import { useTranslations } from '../../../i18n/useTranslations';
import {
  SidecarDetailRecord,
  SidecarOperatingSystem,
} from '../../../types/sidecar';

import { SelectFacility } from '~/shared/components/select-fields/SelectFacility';

type SidecarFormFieldsProps = {
  sidecar?: SidecarDetailRecord | null;
  className?: string;
};

const sidecarFormSelector = (state: SidecarFormState) => ({
  name: state.name,
  operatingSystem: state.operatingSystem,
  operatingSystemVersion: state.operatingSystemVersion,
  facilityId: state.facilityId,
  discoveryEnabled: state.discoveryEnabled,
});

export const SidecarFormFields = ({
  sidecar,
  className,
}: SidecarFormFieldsProps) => {
  const t = useTranslations();

  const selectedFormAtom = React.useMemo(
    () => selectAtom($sidecarForm, sidecarFormSelector, isEqual),
    [],
  );

  const form = useAtomValue(selectedFormAtom);
  const updateForm = useSetAtom($sidecarForm);

  const handleFieldChange = React.useCallback(
    <K extends keyof SidecarFormState>(field: K) =>
      (value: SidecarFormState[K]) => {
        updateForm((prev) => ({
          ...prev,
          [field]: value,
        }));
      },
    [updateForm],
  );

  const operatingSystemOptions = React.useMemo(
    () =>
      Object.values(enumSidecarOperatingSystem).map((v) => ({
        key: v,
        label: v,
      })),
    [],
  );

  const formatSpeed = (value: number | null) =>
    value != null
      ? `${value} ${t.hardwareView.sidecarDetailView.units.mbps}`
      : '\u2013';

  const formatMs = (value: number | null) =>
    value != null
      ? `${value} ${t.hardwareView.sidecarDetailView.units.ms}`
      : '\u2013';

  return (
    <div className={cn('@SidecarFormFields', className)}>
      <ContentSection
        title={t.hardwareView.sidecarDetailView.sections.systemInfo}
        variant="panel"
      >
        <div className="grid gap-4 md:grid-cols-2">
          <TextField
            label={t.hardwareView.sidecarDetailView.fields.name}
            value={form.name}
            onChange={(value) => handleFieldChange('name')(value)}
            placeholder={t.hardwareView.sidecarDetailView.placeholders.name}
            isRequired
          />

          <Select
            label={t.hardwareView.sidecarDetailView.fields.operatingSystem}
            value={form.operatingSystem}
            onChange={(v) =>
              v &&
              handleFieldChange('operatingSystem')(v as SidecarOperatingSystem)
            }
            options={operatingSystemOptions}
          />

          <TextField
            label={
              t.hardwareView.sidecarDetailView.fields.operatingSystemVersion
            }
            value={form.operatingSystemVersion}
            onChange={(value) =>
              handleFieldChange('operatingSystemVersion')(value)
            }
            placeholder={
              t.hardwareView.sidecarDetailView.placeholders
                .operatingSystemVersion
            }
            isRequired
          />

          <div className="flex flex-col gap-1">
            <span className="text-sm text-neutral/60">
              {t.hardwareView.sidecarDetailView.fields.version}
            </span>
            <span className="text-sm font-medium">
              {sidecar?.version ?? '\u2013'}
            </span>
          </div>
        </div>
      </ContentSection>

      {sidecar?.publicKey && (
        <ContentSection
          title={t.hardwareView.sidecarDetailView.sections.security}
          variant="panel"
        >
          <div className="flex flex-col gap-1">
            <span className="text-sm text-neutral/60">
              {t.hardwareView.sidecarDetailView.fields.publicKey}
            </span>
            <span className="font-mono text-sm break-all">
              {sidecar.publicKey}
            </span>
          </div>
        </ContentSection>
      )}

      <ContentSection
        title={t.hardwareView.sidecarDetailView.sections.location}
        variant="panel"
      >
        <div className="grid gap-4 md:grid-cols-2">
          <SelectFacility
            label={t.hardwareView.sidecarDetailView.fields.facility}
            value={form.facilityId}
            onChange={(value) => handleFieldChange('facilityId')(value)}
            placeholder={t.hardwareView.sidecarDetailView.placeholders.facility}
            isRequired
          />
        </div>
      </ContentSection>

      <ContentSection
        title={t.hardwareView.sidecarDetailView.sections.deviceDiscovery}
        variant="panel"
      >
        <div className="flex items-center justify-between">
          <div className="flex flex-col gap-1">
            <span className="text-sm font-medium">
              {t.hardwareView.sidecarDetailView.fields.discoveryEnabled}
            </span>
            <span className="text-sm text-neutral/60">
              {t.hardwareView.sidecarDetailView.descriptions.discoveryEnabled}
            </span>
          </div>
          <Switch
            isSelected={form.discoveryEnabled}
            onChange={(value) => handleFieldChange('discoveryEnabled')(value)}
          />
        </div>
      </ContentSection>

      <ContentSection
        title={t.hardwareView.sidecarDetailView.sections.network}
        variant="panel"
      >
        <div className="grid gap-4 md:grid-cols-3">
          <div className="flex flex-col gap-1">
            <span className="text-sm text-neutral/60">
              {t.hardwareView.sidecarDetailView.fields.localIpAddress}
            </span>
            <span className="font-mono text-sm">
              {sidecar?.localIpAddress ?? '\u2013'}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-sm text-neutral/60">
              {t.hardwareView.sidecarDetailView.fields.remoteIpAddressV4}
            </span>
            <span className="font-mono text-sm">
              {sidecar?.remoteIpAddressV4 ?? '\u2013'}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-sm text-neutral/60">
              {t.hardwareView.sidecarDetailView.fields.remoteIpAddressV6}
            </span>
            <span className="font-mono text-sm">
              {sidecar?.remoteIpAddressV6 ?? '\u2013'}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-sm text-neutral/60">
              {t.hardwareView.sidecarDetailView.fields.linkSpeedUpstream}
            </span>
            <span className="text-sm font-medium">
              {formatSpeed(sidecar?.linkSpeedUpstream ?? null)}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-sm text-neutral/60">
              {t.hardwareView.sidecarDetailView.fields.linkSpeedDownstream}
            </span>
            <span className="text-sm font-medium">
              {formatSpeed(sidecar?.linkSpeedDownstream ?? null)}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-sm text-neutral/60">
              {t.hardwareView.sidecarDetailView.fields.latencyMs}
            </span>
            <span className="text-sm font-medium">
              {formatMs(sidecar?.latencyMs ?? null)}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-sm text-neutral/60">
              {t.hardwareView.sidecarDetailView.fields.jitterMs}
            </span>
            <span className="text-sm font-medium">
              {formatMs(sidecar?.jitterMs ?? null)}
            </span>
          </div>
        </div>
      </ContentSection>

      <ContentSection
        title={t.hardwareView.sidecarDetailView.sections.timestamps}
        variant="panel"
        hideDivider
      >
        <div className="grid gap-4 md:grid-cols-2">
          <div className="flex flex-col gap-1">
            <span className="text-sm text-neutral/60">
              {t.hardwareView.sidecarDetailView.fields.created}
            </span>
            <span className="text-sm font-medium">
              {sidecar?.timestamps.createdAt ? (
                <LocalizedDate date={sidecar.timestamps.createdAt} />
              ) : (
                '-'
              )}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-sm text-neutral/60">
              {t.hardwareView.sidecarDetailView.fields.updated}
            </span>
            <span className="text-sm font-medium">
              {sidecar?.timestamps.updatedAt ? (
                <LocalizedDate date={sidecar.timestamps.updatedAt} />
              ) : (
                '-'
              )}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-sm text-neutral/60">
              {t.hardwareView.sidecarDetailView.fields.connectedAt}
            </span>
            <span className="text-sm font-medium">
              {sidecar?.connectedAt ? (
                <LocalizedDate date={sidecar.connectedAt} />
              ) : (
                '-'
              )}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-sm text-neutral/60">
              {t.hardwareView.sidecarDetailView.fields.lastHeartbeatAt}
            </span>
            <span className="text-sm font-medium">
              {sidecar?.lastHeartbeatAt ? (
                <LocalizedDate date={sidecar.lastHeartbeatAt} />
              ) : (
                '-'
              )}
            </span>
          </div>
        </div>
      </ContentSection>
    </div>
  );
};
